import { hideOnError } from "@/lib/img-fallback";
import { createFileRoute, Link } from "@tanstack/react-router";

import { Layout } from "@/components/Layout";

import { Reveal } from "@/components/Reveal";
import { BookingCtaButton, INQUIRY_CTA_LABEL } from "@/components/cta/BookingCtaButton";

import { lazySection } from "@/lib/lazy-section";
import { MessagesSquare, FileText, Handshake } from "lucide-react";
import { casesByTagQueryOptions } from "@/lib/public-content.queries";
import { GruendeCreategy } from "@/components/wahlen/GruendeCreategy";
import { BarChartAnimation } from "@/components/personalratswahl/BarChartAnimation";
import { RocketAnimation } from "@/components/parteikampagnen/RocketAnimation";

import analyseIlluAsset from "@/assets/parteikampagnen/analyse-strategie-illu.svg.asset.json";
import designAsset from "@/assets/design-2.svg.asset.json";
import socialMediaAsset from "@/assets/social-media-v2.webp.asset.json";
import fotografinAsset from "@/assets/parteikampagnen/fotografin.png.asset.json";
import websiteAsset from "@/assets/website.webp.asset.json";
import teamRioAsset from "@/assets/parteikampagnen/team-rio.png.asset.json";
import michaelBusserAsset from "@/assets/parteikampagnen/michael-busser.png.asset.json";





const ProzessTimeline = lazySection(async () => {
  const m = await import("@/components/buergermeisterwahl/ProzessTimeline");
  return { default: m.ProzessTimeline };
}, { minHeight: 600 });
const ContactCta = lazySection(async () => {
  const m = await import("@/components/parteikampagnen/ContactCta");
  return { default: m.ContactCta };
}, { minHeight: 400 });

export const Route = createFileRoute("/wahlen-gewinnen/parteikampagnen")({
  // Ticket W7: Herkunftskennzeichnung vom Verteiler /wahlkampfagentur (?von=...).
  validateSearch: (search: Record<string, unknown>): { von?: string } => ({
    von: typeof search.von === "string" ? search.von : undefined,
  }),
  loader: ({ context }) => Promise.all([
    context.queryClient.ensureQueryData(casesByTagQueryOptions("parteikampagnen")),
  ]),
  head: () => {
    const title = "Kampagnen für Parteien und Fraktionen | creategy";
    const description =
      "Wahlkampf für Parteien und Fraktionen auf allen Ebenen, von der Strategie bis zum Werbemittel. Überparteilich, mit Erfahrung aus hundert Wahlkämpfen.";
    const url = "https://creategy.de/wahlen-gewinnen/parteikampagnen";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "creategy" },
        { property: "og:locale", content: "de_DE" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ParteikampagnenPage,
});


function ParteikampagnenPage() {


  return (
    <Layout>
      {/* Hero */}
      <section aria-labelledby="hero">
        <div className="section-y-hero mx-auto w-full max-w-[1400px] px-6 md:px-12">
          {/* Mobile-only animation above headline */}
          <div className="mx-auto mb-8 w-full max-w-[420px] md:hidden">
            <BarChartAnimation />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-center lg:gap-16">
            <div className="relative">
              <h1
                id="hero"
                className="heading-h1 text-brand-wahlen"
              >
                Die Spezialagentur für&nbsp;<span className="md:hidden">Parteiwahl&shy;kämpfe.</span><span className="hidden md:inline">Parteiwahlkämpfe.</span>
              </h1>

              {/* Tablet: text+CTA left, animation right; Mobile & Desktop: single column */}
              <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-2 md:items-center md:gap-10 lg:grid-cols-1">
                <div>
                  <p
                    className="fade-in-up font-body text-base leading-relaxed text-foreground md:text-lg"
                    style={{ animationDelay: "160ms" }}
                  >
                    creategy unterstützt Landes- und Bundesverbände bei ihren Wahlkampagnen:
                    strategisch fundiert, professionell umgesetzt und abgestimmt auf die
                    Strukturen Ihrer Partei.
                  </p>
                  <div
                    className="fade-in-up mt-10 flex flex-col items-stretch gap-3 lg:flex-row lg:items-center"
                    style={{ animationDelay: "240ms" }}
                  >
                    <Link
                      to="/kontakt"
                      search={{ anliegen: "wahlkampf" }}
                      className="inline-flex w-full items-center justify-center rounded-md bg-brand-button px-6 py-3 font-display text-sm font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90 lg:w-auto"
                    >
                      {INQUIRY_CTA_LABEL}
                    </Link>
                    <BookingCtaButton variant="secondary" eventPath="creategy/erstgespraech" />
                  </div>
                </div>
                <div className="hidden md:block lg:hidden">
                  <div className="mx-auto w-full max-w-[380px]">
                    <BarChartAnimation />
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop-only animation, right column */}
            <div className="hidden w-full max-w-[420px] lg:ml-auto lg:block">
              <BarChartAnimation />
            </div>
          </div>
        </div>
      </section>


      {/* Warum Verstärkung */}
      <section aria-labelledby="warum-verstaerkung">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div>
            {/* Tablet-only rocket above headline */}
            <div className="mx-auto mb-4 block w-full max-w-[140px] md:max-w-[200px] lg:hidden">
              <RocketAnimation innerScale="lg" />
            </div>
            <Reveal>
              <h2
                id="warum-verstaerkung"
                className="heading-h2 text-brand-wahlen"
              >
                Warum Parteien im Wahljahr Verstärkung brauchen.
              </h2>
            </Reveal>
            <Reveal delay={60}>
              <div className="heading-lead grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
                <div>
                  <p className="font-body text-base leading-relaxed text-foreground md:text-lg">
                    Programmprozess, Kandidatenaufstellung, Gremienbeschlüsse, Materialproduktion, Digitalkampagne: In den Monaten vor der Wahl verdichtet sich die Arbeit einer Geschäftsstelle auf ein Maß, das mit dem laufenden Betrieb kaum zu stemmen ist. Ehrenamtliche tragen viel, doch professionelle Kampagnenarbeit braucht Kapazitäten, die im Parteiverband oft schlicht nicht vorhanden sind.
                  </p>
                  <p className="mt-6 font-body text-base leading-relaxed text-foreground md:text-lg">
                    Genau hier setzen wir an. creategy bringt die Erfahrung aus über zweihundert Wahlkämpfen in Ihre Kampagne, von der strategischen Konzeption bis zur praktischen Umsetzung. Wir arbeiten dort, wo Sie uns brauchen: als strategischer Sparringspartner Ihrer Verbandsspitze oder als verlängerter Arm Ihrer Geschäftsstelle, der Dinge fertig macht.
                  </p>
                </div>
                <div className="ml-auto hidden w-full max-w-[380px] lg:block">
                  <RocketAnimation />
                </div>
              </div>
            </Reveal>
          </div>
        </div>

      </section>






      {/* Bausteine */}
      <section aria-labelledby="bausteine">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">

          <Reveal>
            <h2
              id="bausteine"
              className="heading-h2 text-brand-wahlen"
            >
              Die Bausteine{" "}
              Ihrer Parteikampagne.
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-8 font-body text-base leading-relaxed text-foreground md:text-lg">
              Jeder Parteiverband hat eigene Strukturen, Budgets und Ausgangslagen. Deshalb buchen Sie bei uns nur, was Sie wirklich brauchen. Alle Bausteine kommen aus einer Hand, greifen ineinander und lassen sich flexibel kombinieren.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              { title: "Analyse & Strategie", text: "Der Grundstein jeder Kampagne. Wir analysieren Ihre Ausgangslage, Ihre Zielgruppen und Ihren politischen Wettbewerb und entwickeln eine Kampagnenstrategie,\u00a0 die zum Wahlziel passt.", rightBottomImage: analyseIlluAsset.url, rightBottomImageAlt: "Illustration: Analyse & Strategie" },
              { title: "Kampagnendesign & Print", text: "Wir entwickeln eine Kampagnenlinie, die auffällt und trotzdem zu Ihrer Partei passt. Und wir bringen sie auf die Straße: Plakate, Flyer und Broschüren, druckfertig und aus einem Guss.", image: designAsset.url, imageAlt: "Illustration als Symbol für Kampagnendesign", imageSize: "sm" as const },
              { title: "Social Media & Digitalkampagne", text: "Wahlkampf findet heute auch im Feed statt. Wir entwickeln Content-Formate, Templates und Kampagnenmotive für Ihre Kanäle.", coverImage: socialMediaAsset.url, coverImageAlt: "Haufen roter Like- und Reaktions-Icons als Symbol für Social-Media-Engagement" },
              { title: "Foto- & Videoproduktion", text: "Ein Bild sagt mehr als tausend Worte. Wir produzieren professionelles Bildmaterial und Kampagnenvideos, die Ihre Botschaft transportieren und auf allen Kanälen funktionieren.", rightBottomImage: fotografinAsset.url, rightBottomImageAlt: "Fotografin mit Kamera und Softbox bei einer professionellen Kampagnenproduktion" },
              { title: "Schulung & Trainings", text: "Ihre Kandidierenden und Ehrenamtlichen sind das Gesicht der Kampagne vor Ort. Wir schulen sie, auf Wunsch durch Regierungssprecher a.D. Michael Bußer.", leftBottomImage: michaelBusserAsset.url, leftBottomImageAlt: "Michael Bußer, Regierungssprecher a.D., beim Rhetorik- und Medientraining" },
              { title: "Operative Kampagnenbegleitung", text: "Manchmal fehlt keine Idee, sondern eine Hand, die anpackt. Wir übernehmen die laufende Umsetzung Ihrer Kampagne.", image: teamRioAsset.url, imageAlt: "Rio (creategy) am Laptop – Symbol für operative Kampagnenbegleitung" },
            ].map((b, i) => {
              return (
                <Reveal key={b.title} delay={i * 60} className="h-full">
                  <article className="flex h-[20rem] flex-row overflow-hidden rounded-md bg-brand-wahlen text-background md:h-[24rem]">
                    {b.leftBottomImage || b.rightBottomImage ? (
                      <div className="flex flex-1 flex-col">
                        <div className="px-6 pt-6 md:px-8 md:pt-8">
                          <h3 className="heading-h3 text-background">
                            {b.title}
                          </h3>
                        </div>
                        <div className="mt-4 flex min-h-0 flex-1 flex-row items-stretch gap-4 md:gap-6">
                          {b.leftBottomImage ? (
                            <>
                              <div className="relative w-[45%] shrink-0 self-stretch">
                                <img
                                  src={b.leftBottomImage}
                                  alt={b.leftBottomImageAlt ?? ""}
                                  className="absolute inset-0 block h-full w-full object-contain object-left-bottom"
                                  decoding="async" onError={hideOnError}
                                />
                              </div>
                              <p className="min-w-0 flex-1 pb-6 pr-6 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty md:pb-8 md:pr-8">
                                {b.text}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="min-w-0 flex-1 pb-6 pl-6 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty md:pb-8 md:pl-8">
                                {b.text}
                              </p>
                              <div className="relative w-[45%] shrink-0 self-stretch">
                                <img
                                  src={b.rightBottomImage}
                                  alt={b.rightBottomImageAlt ?? ""}
                                  className="absolute inset-0 block h-full w-full object-contain object-right-bottom"
                                  decoding="async" onError={hideOnError}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-1 flex-col p-6 md:p-8">
                          <h3 className="heading-h3 text-background">
                            {b.title}
                          </h3>
                          <p className="mt-4 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty">
                            {b.text}
                          </p>
                          {b.image ? (
                            <div className="-mx-6 -mb-10 mt-auto flex min-h-0 flex-1 items-end justify-center md:-mx-8 md:-mb-12">
                              <img
                                src={b.image}
                                alt={b.imageAlt ?? ""}
                                className={`block h-auto w-auto max-w-full object-contain object-bottom ${b.imageSize === "sm" ? "max-h-[9.5rem] md:max-h-[12rem]" : "max-h-[11rem] md:max-h-[14rem]"}`}
                                decoding="async" onError={hideOnError}
                              />
                            </div>

                          ) : b.coverImage ? (
                            <div className="-mx-6 -mb-10 mt-auto flex min-h-0 flex-1 items-end overflow-hidden max-h-[12rem] md:-mx-8 md:-mb-12 md:max-h-[16rem]">
                              <img
                                src={b.coverImage}
                                alt={b.coverImageAlt ?? ""}
                                className="block h-full w-full object-cover object-top"
                                decoding="async" onError={hideOnError}
                              />
                            </div>
                          ) : (
                            <div
                              aria-hidden="true"
                              className="mt-auto flex aspect-[4/3] items-center justify-center rounded-md border border-dashed border-border bg-secondary/40"
                            >
                              <span className="font-display text-xs uppercase tracking-wider text-muted-foreground">
                                Bild folgt
                              </span>
                            </div>
                          )}
                        </div>

                      </>
                    )}
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>





      {/* 5 Gründe für creategy – gemeinsame Sektion der Wahlkampf-Seiten */}
      <GruendeCreategy description="Warum Landes- und Bundesverbände sich für creategy entscheiden: fünf Gründe, die in der Kampagne den Unterschied machen." />


      {/* FAQ */}
      {/* Prozess */}
      <section aria-labelledby="prozess">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="prozess" className="heading-h2 text-brand-wahlen">
              In drei Schritten{" "}
              zur Zusammenarbeit.
            </h2>
          </Reveal>

          <div className="mt-16 md:mt-20">
            <ProzessTimeline
              headingColor="text-brand-wahlen"
              steps={[
                {
                  title: "Kostenloses Erstgespräch",
                  text: "Wir lernen Ihren Parteiverband kennen. Sie erzählen uns von Ihrem Wahltermin, Ihrer Ausgangslage und Ihren Zielen. Wir zeigen Ihnen, wie eine professionelle Kampagne für Ihre Partei aussehen kann. Digital oder bei Ihnen in der Geschäftsstelle.",
                  Icon: MessagesSquare,
                },
                {
                  title: "Ihr individuelles Angebot",
                  text: "Auf Basis des Gesprächs entwickeln wir ein maßgeschneidertes Angebot mit genau den Bausteinen, die Ihre Kampagne braucht. Abgestimmt auf Ihre Strukturen, Ihren Zeitplan und Ihr Budget. Auf Wunsch so aufbereitet, dass Sie es direkt in Ihre Gremien geben können.",
                  Icon: FileText,
                },
                {
                  title: "Ihre Entscheidung",
                  text: "Sie entscheiden, ob und in welchem Umfang wir zusammenarbeiten. In Ruhe und mit allen Gremien, die dazugehören. Wenn Sie Ja sagen, legen wir los. Im Zweifel in sieben Tagen.",
                  Icon: Handshake,
                },
              ]}
            />
          </div>
        </div>
      </section>
      <ContactCta />

    </Layout>
  );
}
